import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Box,
  Typography,
  Paper,
  Pagination,
  PaginationItem,
  Select,
  MenuItem
} from '@mui/material'

import ConfirmationModal from '../../mailTemplate/src/components/atomic/molecules/ConfirmationModal'
import { deleteCampaign } from '../../mailTemplate/src/services/B2BServices'
import { useToast } from '../hooks/useToast'

import {
  CampaignTableRow,
  CampaignTableHeader,
  CampaignTableEmptyState,
  CreateCampaignBanner
} from './CampaignTableRow'

const PAGE_SIZE_STORAGE_KEY = 'mc:campaigns:pageSize'
const ALLOWED_PAGE_SIZES = [10, 25, 50, 100]
const DEFAULT_PAGE_SIZE = 25

const CampaignTableList = ({
  data,
  fetchCampaigns,
  campaignReports,
  refreshCampaignReport,
  campaignReportsFetching,
  onCreateCampaign
}) => {
  const { t } = useTranslation()
  const { showError } = useToast()

  const [confirmationModalItem, setConfirmationModalItem] = useState(null)
  const [confirmationModalLoading, setConfirmationModalLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(() => {
    try {
      const saved = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY))
      return ALLOWED_PAGE_SIZES.includes(saved) ? saved : DEFAULT_PAGE_SIZE
    } catch { return DEFAULT_PAGE_SIZE }
  })

  const totalCount = data?.length || 0
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  useEffect(() => {
    if (page > totalPages) setPage(1)
  }, [page, totalPages])

  const pagedData = useMemo(() => {
    if (!data) return []
    const start = (page - 1) * pageSize
    return data.slice(start, start + pageSize)
  }, [data, page, pageSize])

  const rangeStart = totalCount === 0 ? 0 : (page - 1) * pageSize + 1
  const rangeEnd = Math.min(page * pageSize, totalCount)

  const handlePageSizeChange = (e) => {
    const next = Number(e.target.value)
    setPageSize(next)
    setPage(1)
    try { localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(next)) } catch {}
  }

  const handleCampaignDelete = useCallback(async () => {
    setConfirmationModalLoading(true)
    try {
      await deleteCampaign(confirmationModalItem.id)
      setConfirmationModalItem(null)
      fetchCampaigns()
    } catch (err) {
      showError(err || t('mailTemplate.campaigns.deleteModal.toastErr'))
    } finally {
      setConfirmationModalLoading(false)
    }
  }, [confirmationModalItem, fetchCampaigns, showError, t])

  const closeConfirmationModal = useCallback(() => {
    setConfirmationModalItem(null)
  }, [])

  if (!data || data.length === 0) {
    return <CampaignTableEmptyState onCreateCampaign={onCreateCampaign} />
  }

  return (
    <Box>
      <CreateCampaignBanner onCreateCampaign={onCreateCampaign} />

      <Paper
        elevation={0}
        sx={{
          borderRadius: 3,
          border: '1px solid',
          borderColor: 'rgba(0, 0, 0, 0.05)',
          overflow: 'hidden',
          backgroundColor: 'background.paper',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.02), 0 4px 16px rgba(0, 0, 0, 0.04)'
        }}
      >
        <Box sx={{ overflow: 'auto' }}>
          <Box sx={{ minWidth: 1100 }}>
            <CampaignTableHeader />
            {pagedData.map((item, index) => (
              <CampaignTableRow
                key={item.id || index}
                campaignItem={item}
                campaignReport={campaignReports?.[item.id]}
                campaignReportFetching={campaignReportsFetching?.[item.id]}
                setConfirmationModalItem={setConfirmationModalItem}
                refreshCampaignReport={refreshCampaignReport}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 2.5,
            py: 1.25,
            borderTop: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.06)',
            background: 'linear-gradient(180deg, #fafbfc 0%, #f4f6f8 100%)'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 400 }}>
              {t('mailTemplate.campaigns.pagination.rowsPerPage') || 'Rows per page'}
            </Typography>
            <Select
              value={pageSize}
              onChange={handlePageSizeChange}
              size="small"
              dir="ltr"
              sx={{
                height: 28,
                minWidth: 62,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'text.primary',
                backgroundColor: 'background.paper',
                borderRadius: 1,
                '& .MuiSelect-select': {
                  py: 0,
                  height: '28px !important',
                  minHeight: '28px !important',
                  pl: '8px !important',
                  pr: '24px !important',
                  display: 'flex',
                  alignItems: 'center',
                  boxSizing: 'border-box'
                },
                '& .MuiSelect-icon': { right: 4, fontSize: 18, color: 'text.secondary' },
                '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.08)' },
                '&:hover fieldset': { borderColor: 'rgba(0, 150, 136, 0.4) !important' }
              }}
            >
              {ALLOWED_PAGE_SIZES.map((size) => (
                <MenuItem key={size} value={size} sx={{ fontSize: '0.8rem' }}>{size}</MenuItem>
              ))}
            </Select>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', fontWeight: 400, marginInlineStart: 1 }}>
              <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{rangeStart}–{rangeEnd}</span>
              {' '}{t('mailTemplate.campaigns.pagination.of') || 'of'}{' '}
              <span dir="ltr" style={{ unicodeBidi: 'isolate' }}>{totalCount}</span>
            </Typography>
          </Box>

          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, value) => setPage(value)}
            shape="rounded"
            size="small"
            siblingCount={1}
            renderItem={(item) => (
              <PaginationItem
                {...item}
                sx={{
                  fontSize: '0.78rem',
                  fontWeight: 500,
                  minWidth: 30,
                  height: 30,
                  border: 'none',
                  color: 'text.secondary',
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(0, 150, 136, 0.1)',
                    color: 'primary.main',
                    '&:hover': { backgroundColor: 'rgba(0, 150, 136, 0.16)' }
                  }
                }}
              />
            )}
          />
        </Box>
      </Paper>

      <ConfirmationModal
        open={Boolean(confirmationModalItem)}
        close={closeConfirmationModal}
        loading={confirmationModalLoading}
        submit={handleCampaignDelete}
        title={t('mailTemplate.campaigns.deleteModal.title')}
        subTitle={confirmationModalItem?.name}
        text={t('mailTemplate.campaigns.deleteModal.text')}
        submitText={t('mailTemplate.campaigns.deleteModal.submitText')}
        cancelText={t('mailTemplate.campaigns.deleteModal.cancelText')}
        submitBtnColor="error"
      />
    </Box>
  )
}

export default CampaignTableList
